import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from 'react';
import { message, Divider, Modal, Input, ConfigProvider } from 'antd';
import copy from 'copy-to-clipboard';
import { XProvider } from '@ant-design/x';
import type { ConversationsProps } from '@/types/antdx';
import { ExclamationCircleFilled, LikeFilled, LikeOutlined } from '@ant-design/icons';
import { XAiContext } from '@/context/AiProviderContext';
import { XAiProviderProps, XAiProviderHandle } from '@/types/XAiProvider';
import useAgentGenerator from '@/hooks/useAgentGenerator';
import { MOBILE_MAX_WIDTH, API_SUCCESS_CODE } from '@/constants';
import userIcon from '@/assets/user.png';
import { primaryBlue } from '@/styles/common';
import DislikeFeedback from '../DislikeFeedback';
import { addFeedback } from '@/services/api';
import { defaultActions } from '../XAiChatbot';
import type { SendContent } from '@/types';

const { confirm } = Modal;

const XAiProvider = forwardRef<XAiProviderHandle, XAiProviderProps>(({
  url = window.location.origin,
  token,
  config,
  providerId = 'defaultId',
  onSuccess,
  onError,
  onMessage,
  children,
}, ref) => {
  // 使用 useAgentGenerator Hook 管理消息状态

  const { textSpeed = 55 } = config?.chatProps || {};

  const {
    appInfo,
    chat,
    stopChat,
    reChat,
    createNewChat,
    deleteSession,
    updateSession,
    currentSessionId,
    sessionPagination,
    sessionList,
    messages,
    loading,
    setCurrentSession,
    setMessages,
  } = useAgentGenerator({ url, token, config, onError, onSuccess, onMessage, textSpeed });

  const { appName, icon } = appInfo || {};

  useImperativeHandle(ref, () => ({
    chat,
    stopChat,
    reChat,
    getAppInfo: () => appInfo,
    getMessages: () => messages,
    getSessions: () => sessionList,
    setMessages,
    setCurrentSession,
  }), [appInfo, messages, chat, stopChat, reChat, setMessages, setCurrentSession]);

  const [isNarrow, setIsNarrow] = useState<boolean>(false);
  const divRef = useRef<HTMLDivElement>(null);

  // 监听容器宽度变化
  useEffect(() => {
    const div = divRef.current;
    if (!div) return;

    // 创建监听器
    const resizeObserver = new ResizeObserver((entries) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const entry of entries) {
        const { width } = entry.contentRect;
        setIsNarrow(width <= MOBILE_MAX_WIDTH);
      }
    });

    // 开始监听
    resizeObserver.observe(div);

    // 初始化检查
    const initialWidth = div.offsetWidth;
    setIsNarrow(initialWidth <= MOBILE_MAX_WIDTH);

    // 清理函数
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // 发送消息
  const handleSendMessage = (content: SendContent) => {
    chat(content);
  };

  // 清空消息
  const handleCreateNewMessage = () => {
    createNewChat();
  };

  // 工具栏按钮点击
  const handleActions = async (index: number, actionData: any) => {
    const { content, id } = actionData || {};
    const { text = '' } = content;
    switch (index) {
      case 0:
        reChat();
        break;
      case 1:
        copy(text);
        message.success('复制成功');
        break;
      case 2: {
        const idx = messages.findIndex((item) => item.id === id);
        if (idx >= 0) {
          // 删除该条消息及建议类消息
          if (messages.length - idx <= 2) {
            setMessages((prev) => prev.filter((item) => item.id !== id && item.type !== 'SuggestionMessage'));
          } else {
            // 非最新消息 只删除该条消息
            setMessages((prev) => prev.filter((item) => item.id !== id));
          }
          message.success('删除成功');
        }
        break;
      }
      case 3: {
        const { appNo } = config || {};
        const isLike = actionData.isLike ?? 0;
        if (isLike !== 0) {
          message.warning('已收到您的反馈，无需重复提交');
          return;
        }
        if (appNo) {
          const { result } = await addFeedback({
            url,
            token,
            appNo,
            requestId: actionData.raw?.requestId,
            isLike: 1,
          });
          if (result?.code === API_SUCCESS_CODE) {
            message.success('感谢您的反馈');
            setMessages((prev) => {
              return prev.map((item) => {
                if (item.id === id) {
                  return {
                    ...item,
                    isLike: 1,
                  };
                }
                return item;
              });
            });
          } else {
            message.error('反馈失败，请稍后再试');
          }
        }
        break;
      }
    }
  };

  // 建议消息点击
  const handleSuggestMessageClick = (_item: any, id: string) => {
    setMessages((prev: any[]) => prev.filter((m: any) => m.id !== id));
  };

  // 切换会话
  const handleActiveChange = (key: string) => {
    setCurrentSession(key);
  };

  // 删除会话
  const handleDeleteSession = (sessionId: string) => {
    deleteSession(sessionId);
  };

  // 修改会话内容
  const handleUpdateSession = (sessionId: string, title: string) => {
    updateSession(sessionId, title);
  };

  // 会话操作菜单
  const menuConfig: ConversationsProps['menu'] = (conversation) => ({
    items: [
      {
        label: '重命名',
        key: 'edit',
      },
      {
        label: '删除',
        key: 'delete',
        danger: true,
      },
    ],
    onClick: (menuInfo) => {
      menuInfo.domEvent.stopPropagation();
      const { key } = menuInfo;
      switch (key) {
        case 'edit': {
          let inputValue = conversation.label as string || '';
          confirm({
            title: '编辑对话名称',
            icon: null,
            content: (
              <Input
                defaultValue={inputValue}
                autoFocus
                style={{ width: '100%' }}
                onChange={(e) => {
                  inputValue = e.target.value;
                }}
              />
            ),
            okText: '确认',
            cancelText: '取消',
            okButtonProps: {
              style: { background: '#000', borderColor: '#000', color: '#fff' },
            },
            onOk: () => {
              if (!inputValue.trim()) {
                message.error('对话名称不能为空');
                return Promise.reject();
              }
              handleUpdateSession(conversation.key, inputValue);
            },
            onCancel: () => {
              // 静默处理
            },
          });
          break;
        }
        case 'delete':
          confirm({
            title: '确认删除?',
            icon: <ExclamationCircleFilled />,
            content: '该对话内容将被删除无法恢复。',
            okText: '确认删除',
            okType: 'danger',
            cancelText: '取消',
            okButtonProps: {
              style: { background: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' },
            },
            onOk() {
              handleDeleteSession(conversation.key);
            },
            onCancel() {
              //
            },
          });
          break;
      }
    },
  });

  const ToolTip = (messageData: any) => {
    const finalResult = (messageData?.think || []).find((item: any) => item.actionName === 'FINISHED');
    if (!finalResult) return null;
    const { cost, token: tokenCount } = finalResult.extra || {};
    if (!cost && !token) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: '#949494', flex: 1, paddingRight: '40px' }}>
        { (cost !== undefined && cost !== null) && <div>{cost}s</div> }
        { tokenCount && <Divider type="vertical" /> }
        { tokenCount && <div>{ tokenCount } tokens</div> }
      </div>
    );
  };
  // 是否展示清空按钮
  const clearBtnShow = typeof config?.chatProps?.clearBtnShow !== 'undefined'
    ? config.chatProps.clearBtnShow
    : !config?.session?.showSessionList;

  return (
    <ConfigProvider theme={{
      token: {
        colorPrimary: appInfo?.appTheme || primaryBlue,
      },
    }}
    >
      <XProvider>
        <XAiContext.Provider value={{
          avatar: <img src={icon} alt="智能体头像" />,
          userAvatar: <img src={userIcon} alt="用户头像" />,
          emptyStateImage: icon,
          emptyStateText: appName,
          footerTips: '内容由AI生成，无法确保真实准确，仅供参考',
          providerId,
          sessionList,
          sessionTotal: sessionPagination.total,
          token,
          messages,
          loading,
          error: null,
          activeKey: currentSessionId,
          clearBtnShow,
          inputShow: config?.chatProps?.inputShow || true,
          isNarrow,
          messageTooltip: ToolTip,
          messageActions: [...defaultActions, {
            key: 'like',
            tooltip: '赞',
            render: (i, msg) => {
              const isLike = msg.isLike === 1;
              if (isLike) {
                return (
                  <LikeFilled style={{ color: appInfo?.appTheme || primaryBlue }} />
                );
              }
              return <LikeOutlined />;
            },
          }, {
            key: 'dislike',
            tooltip: '踩',
            render: (_i, msg) => {
              const requestId = msg.raw?.requestId;
              return (
                <DislikeFeedback
                  msg={msg}
                  activeColor={appInfo?.appTheme || primaryBlue}
                  onSubmit={async (formData) => {
                    const { result } = await addFeedback({
                      ...formData,
                      requestId,
                      isLike: -1,
                      url,
                      appNo: config?.appNo,
                      token,
                    });
                    if (result?.code === API_SUCCESS_CODE) {
                      message.success('已收到您的反馈，我们会努力做的更好');
                      setMessages((prev) => {
                        return prev.map((item) => {
                          if (item.id === msg.id) {
                            return {
                              ...item,
                              isLike: -1,
                            };
                          }
                          return item;
                        });
                      });
                      return true;
                    }
                    message.error('反馈失败，请稍后再试');
                    return false;
                  }}
                />
              );
            },
          }],
          onSend: handleSendMessage,
          onClear: handleCreateNewMessage,
          onNewChat: handleCreateNewMessage,
          onStop: stopChat,
          menu: menuConfig,
          onActiveChange: handleActiveChange,
          onSuggestMessageClick: handleSuggestMessageClick,
          onMessagesActionsCallback: handleActions,
          isInProvider: true,
        }}
        >
          <div
            ref={divRef}
            style={{
              display: 'flex',
              height: '100%',
              position: 'relative',
            }}
          >
            {children}
          </div>
        </XAiContext.Provider>
      </XProvider>
    </ConfigProvider>
  );
});

export default XAiProvider;
